import { useState } from "react";
import { theme, type as text } from "../theme";
import { frameworkAccent } from "../components/controlStatus";
import { StandardHeader, STANDARD_PAGE_LABELS } from "../components/standards/StandardHeader";
import { ControlGroup, ControlGroupStack } from "../components/standards/ControlGroup";
import { HARMONISED_CLAUSES } from "../components/standards/isoClauses";

// ISO 9002:2016 implementation guidance for the ISO 9001 clauses: reference only, grouped by
// clause in the same collapsible groups as the ISO 9001 clause page.

const GUIDANCE = [
  {
    clause: "4.1",
    title: "Understanding the organisation and its context",
    intent: "Determine the external and internal issues that are relevant to the organisation's purpose and that affect its ability to achieve the intended results of the QMS.",
    guidance: [
      {
        area: "What to do",
        steps: [
          "List internal factors: culture, knowledge, values, performance, resources, and governance structures.",
          "List external factors: regulatory landscape, market conditions, customer expectations, and technological trends.",
          "Review these at least annually or when significant change occurs.",
        ],
      },
    ],
  },
  {
    clause: "4.2",
    title: "Understanding the needs and expectations of interested parties",
    intent: "Identify the interested parties relevant to the QMS and determine the requirements they have that are relevant to the system.",
    guidance: [
      {
        area: "What to do",
        steps: [
          "Identify customers, suppliers, regulators, employees, shareholders, and communities.",
          "For each, determine which of their requirements are relevant to the QMS.",
          "Monitor interested-party requirements and update the list when relationships change.",
        ],
      },
    ],
  },
  {
    clause: "4.3",
    title: "Determining the scope of the QMS",
    intent: "Define the boundaries and applicability of the QMS — which products, services, locations, and functions are included — and document any exclusions.",
    guidance: [
      {
        area: "What to do",
        steps: [
          "Define which products and services are included, and which locations or functions are covered.",
          "Document any exclusions from ISO 9001 clause 8 requirements and justify each.",
          "Make the scope available and maintainable as a documented statement.",
        ],
      },
    ],
  },
  {
    clause: "4.4",
    title: "Quality management system and its processes",
    intent: "Establish, implement, maintain, and continually improve the QMS by defining the processes needed, their sequence, interactions, owners, and performance criteria.",
    guidance: [
      {
        area: "What to do",
        steps: [
          "Map all processes needed to deliver products and services, including inputs, outputs, and sequence.",
          "Assign owners to each process and define the resources, criteria, and methods for each.",
          "Continually evaluate processes and make changes when performance falls short.",
        ],
      },
    ],
  },
  {
    clause: "5",
    title: "Leadership",
    intent: "Top management must actively drive the QMS — not just endorse it — and create conditions for its success.",
    guidance: [
      {
        area: "Leadership and commitment (5.1)",
        steps: [
          "Top management should chair or actively participate in QMS reviews rather than delegating entirely.",
          "Integrate quality objectives with the strategic direction of the business.",
          "Ensure resources are allocated to quality activities in the annual budgeting cycle.",
          "Promote a culture where quality issues are surfaced and resolved, not hidden.",
        ],
      },
      {
        area: "Customer focus (5.1.2)",
        steps: [
          "Establish processes that determine and review customer requirements before accepting orders.",
          "Measure customer satisfaction through surveys, complaint tracking, or return rates.",
          "Act on feedback: link satisfaction data back to process changes.",
        ],
      },
      {
        area: "Quality policy (5.2)",
        steps: [
          "Write a brief, meaningful quality policy — avoid generic statements that could apply to any organisation.",
          "Communicate the policy to all staff, including contractors working under QMS scope.",
          "Review the policy when strategic direction changes.",
        ],
      },
      {
        area: "Roles and responsibilities (5.3)",
        steps: [
          "Assign responsibility for QMS conformity to a named individual, even if shared across a team.",
          "Ensure all process owners understand their quality responsibilities.",
          "Document accountability where there is genuine risk of ambiguity.",
        ],
      },
    ],
  },
  {
    clause: "6",
    title: "Planning",
    intent: "Identify risks and opportunities proactively, set measurable objectives, and plan changes carefully.",
    guidance: [
      {
        area: "Risks and opportunities (6.1)",
        steps: [
          "Use the context analysis from clause 4 as the starting point for risk identification.",
          "Employ a simple risk register: identify the risk, assess likelihood and impact, choose a treatment (accept, mitigate, transfer, avoid).",
          "Verify that planned risk actions are actually implemented and effective.",
        ],
      },
      {
        area: "Quality objectives (6.2)",
        steps: [
          "Set objectives that are measurable and linked to the quality policy.",
          "For each objective, document: what will be done, what resources are needed, who is responsible, and when it will be achieved.",
          "Review objective progress in management review meetings.",
        ],
      },
      {
        area: "Planning changes (6.3)",
        steps: [
          "Before making significant changes to the QMS or its processes, assess potential consequences.",
          "Maintain continuity of the QMS during change — ensure responsibilities are clear throughout transitions.",
          "Communicate planned changes to affected parties before implementation.",
        ],
      },
    ],
  },
  {
    clause: "7",
    title: "Support",
    intent: "Provide the right people, infrastructure, knowledge, and documentation for the QMS to function.",
    guidance: [
      {
        area: "Resources (7.1)",
        steps: [
          "Assess resource adequacy at each management review — are staffing levels, equipment, and facilities sufficient?",
          "Maintain measuring equipment through a calibration programme if measurement is critical to product conformity.",
          "Capture and protect organisational knowledge: document lessons learned, expert know-how, and process wisdom.",
        ],
      },
      {
        area: "Competence (7.2)",
        steps: [
          "Define the competencies required for each role that affects product or service quality.",
          "Conduct gap analysis and plan training or recruitment to close gaps.",
          "Retain records of education, training, and experience for personnel in quality-critical roles.",
        ],
      },
      {
        area: "Awareness (7.3)",
        steps: [
          "Brief all staff on the quality policy and how their specific work contributes to it.",
          "Include quality awareness in induction for new starters and contractors.",
        ],
      },
      {
        area: "Communication (7.4)",
        steps: [
          "Define what quality-related information needs to be communicated, at what frequency, and to whom.",
          "Ensure there is a clear channel for staff to raise quality concerns without fear of reprisal.",
        ],
      },
      {
        area: "Documented information (7.5)",
        steps: [
          "Maintain a document register: title, owner, version, approval date.",
          "Protect documents from unintended alteration and ensure only current versions are in use.",
          "Retain records long enough to demonstrate QMS effectiveness — define retention periods by record type.",
        ],
      },
    ],
  },
  {
    clause: "8",
    title: "Operation",
    intent: "Plan, control, and deliver products and services consistently, managing suppliers and nonconformances along the way.",
    guidance: [
      {
        area: "Operational planning and control (8.1)",
        steps: [
          "Establish acceptance criteria for each process output before production or service delivery begins.",
          "Control planned processes using documented procedures, work instructions, or checklists as appropriate.",
          "Retain records to demonstrate that processes have been carried out as planned.",
        ],
      },
      {
        area: "Requirements for products and services (8.2)",
        steps: [
          "Review customer requirements before accepting orders — including implied requirements not explicitly stated.",
          "Resolve any differences between contract requirements and earlier quotations before committing.",
          "Amend records when requirements change; communicate changes to relevant staff.",
        ],
      },
      {
        area: "Design and development (8.3)",
        steps: [
          "Plan design stages with gates: inputs, outputs, reviews, verification, and validation.",
          "Manage design changes: assess impact, approve, document, and re-verify where necessary.",
        ],
      },
      {
        area: "External providers (8.4)",
        steps: [
          "Evaluate and select suppliers based on their ability to supply conforming products or services.",
          "Communicate quality requirements clearly in purchase orders or contracts.",
          "Monitor supplier performance and take action when it falls short.",
        ],
      },
      {
        area: "Production and service provision (8.5)",
        steps: [
          "Identify and control all product throughout production to avoid mix-ups — use lot numbers, labels, or tags.",
          "Preserve product condition (packaging, storage, handling) until delivery.",
          "Obtain customer property with care; report any damage or loss promptly.",
        ],
      },
      {
        area: "Nonconforming outputs (8.7)",
        steps: [
          "Segregate and clearly identify nonconforming product or service outcomes immediately.",
          "Decide disposition: rework, scrap, concession, or return — and document the decision.",
          "If nonconforming output is discovered after delivery, assess impact and take appropriate action.",
        ],
      },
    ],
  },
  {
    clause: "9",
    title: "Performance Evaluation",
    intent: "Systematically measure, audit, and review QMS performance so that management can make informed decisions.",
    guidance: [
      {
        area: "Monitoring and measurement (9.1)",
        steps: [
          "Define key quality metrics and the frequency at which each is measured.",
          "Measure customer satisfaction through at least one method — survey, complaint rate, repeat orders, or NPS.",
          "Analyse data to identify trends, not just point-in-time snapshots.",
        ],
      },
      {
        area: "Internal audit (9.2)",
        steps: [
          "Produce an audit programme covering all QMS processes and clauses over a defined cycle (typically annual).",
          "Select auditors who are independent of the area being audited.",
          "Report audit findings formally; track corrective actions to closure.",
        ],
      },
      {
        area: "Management review (9.3)",
        steps: [
          "Hold management reviews at planned intervals — at least annually, more frequently if the QMS is immature.",
          "Agenda must include: audit results, customer feedback, process performance, supplier performance, risk status, and objective progress.",
          "Record decisions and action owners; follow up at the next review.",
        ],
      },
    ],
  },
  {
    clause: "10",
    title: "Improvement",
    intent: "Turn findings from evaluation into lasting improvements — correct what went wrong and raise the bar continually.",
    guidance: [
      {
        area: "Nonconformity and corrective action (10.2)",
        steps: [
          "React to nonconformity: contain, correct, and then investigate root cause.",
          "Choose a corrective action that addresses the root cause, not just the symptom.",
          "Verify effectiveness after implementation — did the nonconformity recur?",
          "Update risks, opportunities, or documented information if the investigation reveals a systemic gap.",
        ],
      },
      {
        area: "Continual improvement (10.3)",
        steps: [
          "Use outputs from management review, audits, customer feedback, and data analysis to identify improvement opportunities.",
          "Prioritise improvements by impact on customer satisfaction and QMS performance.",
          "Track improvement initiatives to completion; celebrate progress to build a quality culture.",
        ],
      },
    ],
  },
];

export function Iso9002Page() {
  const accent = frameworkAccent("iso9001");
  const [expanded, setExpanded] = useState<Set<number>>(new Set([4]));
  const toggle = (n: number) => setExpanded((prev) => { const s = new Set(prev); if (s.has(n)) s.delete(n); else s.add(n); return s; });

  return (
    <div className="page-pad">
      <StandardHeader
        frameworkId="iso9001"
        page={STANDARD_PAGE_LABELS.guidelines}
        subtitle="ISO 9002:2016 guidance on the intent of each ISO 9001 requirement, with examples of steps that meet it: a companion to the clauses page."
      />

      <ControlGroupStack>
        {HARMONISED_CLAUSES.map((c) => {
          const sections = GUIDANCE.filter((s) => parseInt(s.clause, 10) === c.num);
          if (sections.length === 0) return null;
          return (
            <ControlGroup
              key={c.num}
              title={`Clause ${c.num}: ${c.title}`}
              accent={accent}
              count={sections.length}
              noun="requirement"
              expanded={expanded.has(c.num)}
              onToggle={() => toggle(c.num)}
            >
              {sections.map((section) => (
                <div key={section.clause} style={{ borderTop: `1px solid ${theme.color.border}`, padding: "12px 16px" }}>
                  <div style={{ display: "flex", gap: 10, alignItems: "baseline", flexWrap: "wrap" }}>
                    <span style={{ ...text.ref, fontWeight: 600, color: theme.color.muted, minWidth: 52 }}>{section.clause}</span>
                    <span style={{ ...text.body, fontWeight: 600, color: theme.color.heading }}>{section.title}</span>
                  </div>
                  <div style={{ ...text.hint, margin: "6px 0 8px", paddingLeft: 62 }}>{section.intent}</div>
                  {section.guidance.map((g) => (
                    <div key={g.area} style={{ paddingLeft: 62, marginBottom: 6 }}>
                      <div style={text.label}>{g.area}</div>
                      <ul style={{ margin: 0, paddingLeft: 18, display: "flex", flexDirection: "column", gap: 3 }}>
                        {g.steps.map((step, i) => <li key={i} style={text.hint}>{step}</li>)}
                      </ul>
                    </div>
                  ))}
                </div>
              ))}
            </ControlGroup>
          );
        })}
      </ControlGroupStack>
    </div>
  );
}
